import type { EditorController } from "./controller";

// Closure variable to hold the controller instance without proxying it
let editorController: EditorController | null = null;

export interface GenericState {
  [key: string]: any;
}

export interface ActiveNode {
  type: string; // 'table', 'image', etc.
  state: GenericState;
}

export interface EditorStore {
  delegate: any;
  loaded: boolean;
  i18n: any;
  status: "saved" | "saving" | "unsaved" | "error" | "offline";
  tocHTML: string;
  linkModalOpen: boolean;
  linkUrl: string;
  activeMarks: { [key: string]: any };
  activeColor: string;
  manualFontSize: string;
  history: { canUndo: boolean; canRedo: boolean };
  activeNodes: { [key: string]: any }; // Map of type -> state
  stats: { words: number; chars: number };

  // Methods
  mount(el: HTMLElement, config: any): void;
  updateState(marks: any, attrs: any, stats: any, history: any, activeNodes: any): void;
  getStatusMessage(): string;

  // Commands
  undo(): void;
  redo(): void;
  canUndo(): boolean;
  canRedo(): boolean;
  toggleBold(): void;
  toggleItalic(): void;
  toggleUnderline(): void;
  toggleHighlight(): void;
  setColor(color: string): void;
  setTextAlign(align: string): void;
  toggleHeading(level: number): void;
  toggleBulletList(): void;
  toggleOrderedList(): void;
  triggerImageUpload(): void;
  insertTable(rows: number, cols: number): void;
  insertToC(): void;
  openLinkModal(): void;
  closeLinkModal(): void;
  saveLink(): void;
  mergeCells(): void;
  splitCell(): void;
  deleteTable(): void;
  addColumnBefore(): void;
  addColumnAfter(): void;
  deleteColumn(): void;
  addRowBefore(): void;
  addRowAfter(): void;
  deleteRow(): void;
  toggleHeaderRow(): void;
  toggleHeaderColumn(): void;
  toggleHeaderCell(): void;
  setVerticalAlign(align: string): void;

  // Spacing
  unsetFontSize(): void;
  setLineHeight(height: string): void;
  setParagraphSpacing(spacing: string): void;
  setFontSize(size: string): void;

  // Image Actions
  setImageAlign(align: string): void;
  setImageWidth(width: string): void;
  setImageWrap(wrap: boolean): void;
  setImageMargin(margin: string): void;
  deleteImage(): void;
}

export function setupStore(Alpine: any, ControllerClass: typeof EditorController) {
  const store = Alpine.store("editor");
  if (!store) {
    console.error("Editor store not found - components.js must load first");
    return;
  }

  // Eager load i18n from global window object
  if (window.EDITOR_I18N) {
    store.i18n = window.EDITOR_I18N;
  }

  // Create the Strategy object with real implementations
  const strategy = {
    mount(element, config) {
      // A boosted body swap re-mounts without a page reload, so the previous
      // controller (and its save timers) is still alive. Same document (e.g.
      // after a version restore) → drop its pending save, as the old full
      // reload did; another document → flush it so no edit is lost.
      if (editorController) {
        editorController.teardown(editorController.config?.saveUrl !== config?.saveUrl);
      }
      // Instantiate Controller
      editorController = new ControllerClass(element, store, config);
      // Set i18n from config if provided
      if (config.i18n) store.i18n = config.i18n;
    },

    updateState(marks, attrs, stats, history, activeNodes) {
      store.activeMarks = marks;
      store.activeColor = attrs.color;
      store.stats = stats;
      store.history = history;
      store.activeNodes = activeNodes; // New structural context
    },

    // History
    undo() {
      editorController?.undo();
    },
    redo() {
      editorController?.redo();
    },
    canUndo() {
      return editorController?.canUndo() ?? false;
    },
    canRedo() {
      return editorController?.canRedo() ?? false;
    },

    // Typography
    toggleBold() {
      editorController?.toggleBold();
    },
    toggleItalic() {
      editorController?.toggleItalic();
    },
    toggleUnderline() {
      editorController?.toggleUnderline();
    },
    toggleHighlight() {
      editorController?.toggleHighlight();
    },
    setColor(e) {
      editorController?.setColor(e.target.value);
    },

    // Alignment
    setTextAlign(align) {
      editorController?.setTextAlign(align);
    },

    // Headings
    toggleHeading(level) {
      editorController?.toggleHeading(level);
    },

    // Lists
    toggleBulletList() {
      editorController?.toggleBulletList();
    },
    toggleOrderedList() {
      editorController?.toggleOrderedList();
    },

    // Insert
    triggerImageUpload() {
      editorController?.triggerImageUpload();
    },
    insertTable(rows, cols) {
      editorController?.insertTable(rows, cols);
    },
    insertToC() {
      editorController?.insertToC();
    },

    // Link Handling
    openLinkModal() {
      store.linkUrl = editorController?.getLinkUrl() || "";
      store.linkModalOpen = true;
      setTimeout(() => {
        const input = document.querySelector<HTMLInputElement>("#link-url-input");
        if (input) input.focus();
      }, 50);
    },
    saveLink() {
      editorController?.setLink(store.linkUrl);
      store.linkModalOpen = false;
    },

    // Table Actions
    mergeCells() {
      editorController?.mergeCells();
    },
    splitCell() {
      editorController?.splitCell();
    },
    deleteTable() {
      editorController?.deleteTable();
    },
    addColumnBefore() {
      editorController?.addColumnBefore();
    },
    addColumnAfter() {
      editorController?.addColumnAfter();
    },
    deleteColumn() {
      editorController?.deleteColumn();
    },
    addRowBefore() {
      editorController?.addRowBefore();
    },
    addRowAfter() {
      editorController?.addRowAfter();
    },
    deleteRow() {
      editorController?.deleteRow();
    },
    toggleHeaderRow() {
      editorController?.toggleHeaderRow();
    },
    toggleHeaderColumn() {
      editorController?.toggleHeaderColumn();
    },
    toggleHeaderCell() {
      editorController?.toggleHeaderCell();
    },
    setVerticalAlign(align) {
      editorController?.setVerticalAlign(align);
    },

    // Spacing
    setLineHeight(height) {
      editorController?.setLineHeight(height);
    },
    setParagraphSpacing(spacing) {
      editorController?.setParagraphSpacing(spacing);
    },
    setFontSize(size) {
      editorController?.setFontSize(size);
    },
    unsetFontSize() {
      editorController?.unsetFontSize();
    },

    // Image Actions
    setImageAlign(align) {
      editorController?.setImageAlign(align);
    },
    setImageWidth(width) {
      editorController?.setImageWidth(width);
    },
    setImageWrap(wrap) {
      editorController?.setImageWrap(wrap);
    },
    setImageMargin(show) {
      editorController?.setImageMargin(show);
    },
    deleteImage() {
      editorController?.deleteImage();
    },
  };

  // Assign Strategy to store - this triggers Alpine reactivity
  store.delegate = strategy;
  store.loaded = true;
}
